import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, AlertTriangle, Users, ArrowRight } from "lucide-react";

/**
 * Services — the official 14-service catalogue of THOM Verify™.
 *
 * Kept intentionally text-first and enterprise-serious: every card states
 * the service, its inclusions, its target audience where relevant, and —
 * critically — its `limitation` clause so the scope of THOM Verify's role
 * is never overstated.
 */
export default function Services() {
  const [data, setData] = useState({ services: [], philosophy: "" });
  const [quoteFor, setQuoteFor] = useState(null);
  const [form, setForm] = useState({ asset_ref: "", location: "", priority: "normal", message: "" });
  const [busy, setBusy] = useState(false);
  const { user } = useAuth() || {};
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/services/catalog").then(r => setData(r.data)).catch(() => {});
  }, []);

  const openQuote = (svc) => {
    if (!user) {
      toast.error("Please sign in to request a service quote.");
      navigate("/login");
      return;
    }
    setForm({ asset_ref: "", location: "", priority: "normal", message: "" });
    setQuoteFor(svc);
  };

  const submitQuote = async () => {
    if (!quoteFor) return;
    setBusy(true);
    try {
      const r = await api.post(`/services/${quoteFor.slug}/quote`, form);
      const caseNumber = r.data?.case?.case_number || "created";
      toast.success(`Quote received · ${caseNumber}`);
      setQuoteFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit request.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Navbar />

      {/* Hero */}
      <section className="border-b border-border bg-card">
        <div className="max-w-[1400px] mx-auto px-6 py-14">
          <div className="kicker">THOM Verify™ · Services Catalogue</div>
          <h1 className="display text-4xl sm:text-5xl lg:text-6xl mt-2 leading-[0.95] max-w-3xl">
            Independent verification across vehicles, property, land, projects, and enterprise assets.
          </h1>
          <p className="mt-5 max-w-3xl text-muted-foreground leading-relaxed">
            {data.philosophy}
          </p>
        </div>
      </section>

      {/* Catalogue */}
      <section className="max-w-[1400px] mx-auto px-6 py-14">
        <div className="grid md:grid-cols-2 gap-5" data-testid="services-grid">
          {data.services.map(s => (
            <article
              key={s.slug}
              className="border border-border bg-card p-6 rounded-sm flex flex-col gap-4"
              data-testid={`svc-${s.slug}`}
            >
              <header>
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  Service {String(s.n).padStart(2, "0")}
                </div>
                <h2 className="display text-2xl mt-1 leading-tight">{s.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.kicker}</p>
              </header>

              <div>
                <div className="kicker">Included</div>
                <ul className="mt-2 grid sm:grid-cols-2 gap-x-3 gap-y-1 text-sm">
                  {s.services.map(item => (
                    <li key={item} className="flex items-start gap-2">
                      <ShieldCheck className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {s.audience?.length > 0 && (
                <div>
                  <div className="kicker flex items-center gap-1.5"><Users className="h-3 w-3" /> Suitable for</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {s.audience.map(a => (
                      <span key={a} className="text-[10px] mono uppercase tracking-widest px-2 py-0.5 border border-border rounded-sm">{a}</span>
                    ))}
                  </div>
                </div>
              )}

              {s.note && (
                <div className="text-xs text-muted-foreground bg-secondary/50 border-l-2 border-primary/40 px-3 py-2">
                  {s.note}
                </div>
              )}

              <div className="mt-auto pt-4 border-t border-border/60 text-xs leading-relaxed">
                <div className="kicker flex items-center gap-1.5 text-amber-700"><AlertTriangle className="h-3 w-3" /> Limitation</div>
                <p className="mt-1.5 text-muted-foreground">{s.limitation}</p>
              </div>
              <div className="pt-3 flex items-center justify-between">
                <div className="mono text-xs">
                  {s.price_usd ? <span>From <span className="text-primary font-semibold">${s.price_usd}</span> <span className="text-muted-foreground">USD</span></span> : <span className="text-muted-foreground">Contact for pricing</span>}
                </div>
                <Button size="sm" onClick={() => openQuote(s)} data-testid={`btn-request-${s.slug}`}>
                  Request this service <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Philosophy footer */}
      <section className="border-t border-border bg-secondary/30">
        <div className="max-w-[1400px] mx-auto px-6 py-12">
          <div className="kicker">Service Philosophy</div>
          <p className="mt-3 text-sm leading-relaxed max-w-4xl text-muted-foreground">{data.philosophy}</p>
          <div className="mt-6 flex gap-2">
            <Link to="/marketplace" className="btn-outline">Browse verified vehicles <ArrowRight className="h-3.5 w-3.5" /></Link>
            <Link to="/network/register" className="btn-outline">Register your organisation</Link>
          </div>
        </div>
      </section>

      <Footer />

      <Dialog open={!!quoteFor} onOpenChange={(o) => !o && setQuoteFor(null)}>
        <DialogContent className="max-w-lg" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle>{quoteFor?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs mono uppercase tracking-widest">Asset reference (VIN, property title, project name)</Label>
              <Input value={form.asset_ref} onChange={(e) => setForm({ ...form, asset_ref: e.target.value })} data-testid="quote-asset-ref" />
            </div>
            <div>
              <Label className="text-xs mono uppercase tracking-widest">Location</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City, region" data-testid="quote-location" />
            </div>
            <div>
              <Label className="text-xs mono uppercase tracking-widest">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="quote-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low (7d SLA)</SelectItem>
                  <SelectItem value="normal">Normal (4d SLA)</SelectItem>
                  <SelectItem value="high">High (48h SLA)</SelectItem>
                  <SelectItem value="urgent">Urgent (24h SLA)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mono uppercase tracking-widest">Notes for the ops team</Label>
              <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="quote-message" />
            </div>
            <div className="text-xs text-muted-foreground border-t pt-3">
              From <span className="text-foreground font-semibold">${quoteFor?.price_usd} USD</span>. Final quote confirmed after review. An invoice + case will be created; payment secures your SLA.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuoteFor(null)}>Cancel</Button>
            <Button onClick={submitQuote} disabled={busy} data-testid="quote-submit">
              {busy ? "Submitting…" : "Submit request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
